// Starter Tape listen-back notes — turns a finished tape song into small,
// friendly, rule-based observations about what the learner played. These are
// notes, not grades: plain, warm, short sentences, no exclamation marks, no
// emoji. Pure data + functions only (no audio, no React), same as
// starterTape.js.
//
// `checkMust` answers whether a tape's one required move happened, keyed by
// `tape.must.id` (see starterTapeCurriculum.js). `listenBack` builds the
// three-note script (what you did, why it sounds that way, what to try next)
// keyed by `tape.id`.

import {
  HAT,
  INSTRUMENTS,
  KICK,
  SNARE,
  chordInBar,
  diatonicChord,
  noteLetter,
  scaleDegree,
  soundOf,
  stepSeconds,
} from "./starterTape";

const pc = (n) => ((n % 12) + 12) % 12;

const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/* ---------- Intervals ---------- */

const INTERVALS = {
  0: { name: "the same note", feel: "one sound, just louder" },
  1: { name: "a half step", feel: "tense and crunchy" },
  2: { name: "a whole step", feel: "a gentle rub" },
  3: { name: "a minor third", feel: "soft and wistful" },
  4: { name: "a major third", feel: "bright and warm" },
  5: { name: "a fourth", feel: "open and a little hollow" },
  6: { name: "a tritone", feel: "restless and unstable" },
  7: { name: "a fifth", feel: "open and stable" },
  8: { name: "a minor sixth", feel: "bittersweet" },
  9: { name: "a major sixth", feel: "warm and sweet" },
  10: { name: "a minor seventh", feel: "dreamy and unresolved" },
  11: { name: "a major seventh", feel: "shimmering and tense" },
  12: { name: "an octave", feel: "so blended it almost sounds like one note" },
};

// Names the distance between two notes, folding anything past an octave down
// into one (a nonzero multiple of 12 semitones still reads as "an octave",
// never "the same note").
export const intervalName = (semitones) => {
  const abs = Math.abs(semitones);
  let key = abs > 12 ? abs % 12 : abs;
  if (key === 0 && abs !== 0) key = 12;
  return INTERVALS[key];
};

/* ---------- Notes & pairs ---------- */

// e.g. "A · 1"; a note outside the scale is just its letter.
export const noteLabel = (song, midi) => {
  const degree = scaleDegree(midi, song.rootMidi, song.scale);
  return degree === null ? noteLetter(midi) : `${noteLetter(midi)} · ${degree}`;
};

const overlaps = (a, b) => a.step < b.step + b.length && b.step < a.step + a.length;

// Every distinct pair of different pitches that sound at the same time,
// across all non-muted tracks, sorted by the lower pitch then the higher.
export const soundingPairs = (song) => {
  const notes = [];
  song.tracks.forEach((t) => {
    if (t.muted) return;
    const inst = INSTRUMENTS[t.instrument];
    if (inst && inst.unpitched) return;
    t.notes.forEach((n) => notes.push(n));
  });

  const found = new Map();
  for (let i = 0; i < notes.length; i += 1) {
    for (let j = i + 1; j < notes.length; j += 1) {
      const a = notes[i];
      const b = notes[j];
      if (a.pitch === b.pitch || !overlaps(a, b)) continue;
      const low = Math.min(a.pitch, b.pitch);
      const high = Math.max(a.pitch, b.pitch);
      found.set(`${low}-${high}`, { low, high, semitones: high - low });
    }
  }
  return Array.from(found.values()).sort((a, b) => a.low - b.low || a.high - b.high);
};

// The note that starts latest on the track; ties go to the lowest pitch.
export const lastNote = (track) => {
  if (!track.notes.length) return null;
  return track.notes.reduce((best, n) => {
    if (!best) return n;
    if (n.step > best.step) return n;
    if (n.step === best.step && n.pitch < best.pitch) return n;
    return best;
  }, null);
};

// The note that starts earliest on the track; ties go to the lowest pitch.
const firstNote = (track) => {
  if (!track.notes.length) return null;
  return track.notes.reduce((best, n) => {
    if (!best) return n;
    if (n.step < best.step) return n;
    if (n.step === best.step && n.pitch < best.pitch) return n;
    return best;
  }, null);
};

// How many bars of the loop have no note sounding on this track at all.
export const emptyBars = (song, trackId) => {
  const track = song.tracks.find((t) => t.id === trackId);
  if (!track) return song.bars;
  let count = 0;
  for (let bar = 0; bar < song.bars; bar += 1) {
    const start = bar * song.stepsPerBar;
    const end = start + song.stepsPerBar;
    const sounding = track.notes.some((n) => n.step < end && start < n.step + n.length);
    if (!sounding) count += 1;
  }
  return count;
};

// The first track on this instrument, or null.
const findTrack = (song, instrument) =>
  song.tracks.find((t) => t.instrument === instrument) || null;

// A percent for a 0–1 value, rounded to the nearest 10 ("70%").
const pct = (x) => Math.round(x * 10) * 10;

// How many bars have a pad chord and a pure arpeggio in the bell (at least
// 3 bell notes starting in the bar, every one a chord tone), and how many
// bars have a pad chord at all.
const arpeggioStats = (song) => {
  const padTrack = findTrack(song, "pad");
  const bellTrack = findTrack(song, "bell");
  let count = 0;
  let chordBars = 0;
  if (padTrack) {
    for (let bar = 0; bar < song.bars; bar += 1) {
      const chord = chordInBar(song, padTrack.id, bar);
      if (!chord) continue;
      chordBars += 1;
      if (!bellTrack) continue;
      const start = bar * song.stepsPerBar;
      const end = start + song.stepsPerBar;
      const starting = bellTrack.notes.filter((n) => n.step >= start && n.step < end);
      if (starting.length >= 3 && starting.every((n) => chord.pcs.includes(pc(n.pitch)))) {
        count += 1;
      }
    }
  }
  return { count, chordBars };
};

/* ---------- Musts ---------- */

// Whether a tape's one required move happened, and a hint for when it hasn't.
export const checkMust = (mustId, song) => {
  switch (mustId) {
    case "holdLong": {
      const padTrack = song.tracks.find((t) => t.instrument === "pad");
      const met = !!padTrack && padTrack.notes.some((n) => n.length >= song.stepsPerBar);
      return {
        met,
        hint: met ? "" : "Stretch a note to at least 1 bar so there's time to hear it swell.",
      };
    }
    case "twoTogether": {
      const met = soundingPairs(song).length > 0;
      return {
        met,
        hint: met ? "" : "Place a second note that overlaps the first one in time.",
      };
    }
    case "endOnRoot": {
      const bellTrack = song.tracks.find((t) => t.instrument === "bell");
      const last = bellTrack ? lastNote(bellTrack) : null;
      const rootLabel = noteLabel(song, song.rootMidi);
      if (!last) {
        return { met: false, hint: `Add a bell melody, then end it on ${rootLabel}.` };
      }
      const met = pc(last.pitch) === pc(song.rootMidi);
      return {
        met,
        hint: met ? "" : `Move the last bell note to ${rootLabel} and listen to it settle.`,
      };
    }
    case "kickOnOne": {
      const drumsTrack = song.tracks.find((t) => t.instrument === "drums");
      const notes = drumsTrack ? drumsTrack.notes : [];
      let hits = 0;
      for (let bar = 0; bar < song.bars; bar += 1) {
        const start = bar * song.stepsPerBar;
        if (notes.some((n) => n.pitch === KICK && n.step === start)) hits += 1;
      }
      const met = hits === song.bars;
      let hint = "";
      if (!met) {
        hint =
          hits === 0
            ? "Click the Kick row on the first square of each bar."
            : "Some bars are missing a kick on beat 1. Look for the first square after each thick line.";
      }
      return { met, hint };
    }
    case "bassOnRoot": {
      const bassTrack = song.tracks.find((t) => t.instrument === "bass");
      const rootLabel = noteLabel(song, song.rootMidi);
      const first = bassTrack ? firstNote(bassTrack) : null;
      if (!first) {
        return {
          met: false,
          hint: `Add a bass note at the very start and put it on ${rootLabel}.`,
        };
      }
      const met = pc(first.pitch) === pc(song.rootMidi);
      return {
        met,
        hint: met ? "" : `Move the first bass note to ${rootLabel} and hear the loop settle.`,
      };
    }
    case "bothChords": {
      const padTrack = song.tracks.find((t) => t.instrument === "pad");
      let hasFirst = false;
      let hasSecond = false;
      if (padTrack) {
        for (let bar = 0; bar < song.bars; bar += 1) {
          const chord = chordInBar(song, padTrack.id, bar);
          if (!chord) continue;
          if (chord.index === 0) hasFirst = true;
          if (chord.index === 5) hasSecond = true;
        }
      }
      const met = hasFirst && hasSecond;
      if (met) return { met, hint: "" };
      if (!hasFirst && !hasSecond) {
        return { met, hint: "Pick a chord for each bar in the chord strip above the pad." };
      }
      const missingIndex = hasFirst ? 5 : 0;
      const missingName = diatonicChord(song, missingIndex).name;
      return { met, hint: `Now give at least one bar the other chord, ${missingName}.` };
    }
    case "bigSpace": {
      const padTrack = findTrack(song, "pad");
      const met = !!padTrack && soundOf(padTrack).reverb > 0.5;
      return { met, hint: met ? "" : "Drag the pad's reverb past the middle." };
    }
    case "slowSweep": {
      const padTrack = findTrack(song, "pad");
      const sound = soundOf(padTrack || { instrument: "pad" });
      const met = sound.attack >= 1 && !!sound.sweep;
      let hint = "";
      if (!met) {
        hint = sound.attack < 1
          ? "Slide the pad's swell past 1 second."
          : "Turn on the pad's sweep.";
      }
      return { met, hint };
    }
    case "fourChords": {
      const padTrack = findTrack(song, "pad");
      const present = new Set();
      if (padTrack) {
        for (let bar = 0; bar < song.bars; bar += 1) {
          const chord = chordInBar(song, padTrack.id, bar);
          if (chord) present.add(chord.index);
        }
      }
      const want = [0, 5, 2, 6];
      const missing = want.filter((i) => !present.has(i));
      const met = missing.length === 0;
      if (met) return { met, hint: "" };
      const names = missing.map((i) => diatonicChord(song, i).name);
      return { met, hint: `Still missing: ${joinList(names)}.` };
    }
    case "arpeggio": {
      const bellTrack = findTrack(song, "bell");
      if (!bellTrack || bellTrack.notes.length === 0) {
        return {
          met: false,
          hint: "Press Arpeggiate to fill the bell with chord notes, or place them by hand.",
        };
      }
      const { count } = arpeggioStats(song);
      const met = count >= 2;
      return {
        met,
        hint: met ? "" : "The bell needs two bars where every note belongs to that bar's chord.",
      };
    }
    case "susChord": {
      const padTrack = findTrack(song, "pad");
      let met = false;
      if (padTrack) {
        for (let bar = 0; bar < song.bars; bar += 1) {
          const chord = chordInBar(song, padTrack.id, bar);
          if (chord && chord.variant) {
            met = true;
            break;
          }
        }
      }
      return { met, hint: met ? "" : "Pick a chord, then press sus2 or sus4 under it." };
    }
    case "textureArc": {
      const sections = song.sections;
      if (!Array.isArray(sections) || sections.length === 0) {
        return { met: false, hint: "Switch a lane off in at least one section." };
      }
      const counts = sections.map((sec) => {
        const start = sec.startBar * song.stepsPerBar;
        const end = start + sec.bars * song.stepsPerBar;
        return song.tracks.filter(
          (t) => !sec.off.includes(t.id) && t.notes.some((n) => n.step >= start && n.step < end)
        ).length;
      });
      const met = !counts.every((c) => c === counts[0]);
      return { met, hint: met ? "" : "Switch a lane off in at least one section." };
    }
    case "transmission": {
      const count = song.tracks.filter((t) => t.notes.length > 0).length;
      const met = count >= 3;
      return { met, hint: met ? "" : "Add notes to at least three lanes." };
    }
    default:
      return { met: true, hint: "" };
  }
};

/* ---------- Listen-back scripts ---------- */

const PAIR_SUGGESTIONS = [7, 12, 2];

// The next pair to try: the first of a fifth, an octave, or a whole step
// that hasn't sounded yet, or a closing line once all three have.
const pairsNudge = (song, pairs) => {
  const present = new Set(pairs.map((p) => p.semitones));
  const root = song.rootMidi;
  for (let i = 0; i < PAIR_SUGGESTIONS.length; i += 1) {
    const semitones = PAIR_SUGGESTIONS[i];
    if (present.has(semitones)) continue;
    if (semitones === 7) {
      return `Next, try ${noteLabel(song, root)} with ${noteLabel(song, root + 7)} (a fifth) and hear how open it sounds.`;
    }
    if (semitones === 12) {
      return `Next, try ${noteLabel(song, root)} with the ${noteLetter(root)} above it (an octave).`;
    }
    return `Next, try ${noteLabel(song, root + 5)} with ${noteLabel(song, root + 7)} (a whole step) and listen for the rub.`;
  }
  return "You've heard open pairs and rubbing pairs. Notice which ones you like best.";
};

const holdNotes = (song, must) => {
  const padTrack = song.tracks.find((t) => t.instrument === "pad");
  const longest = padTrack
    ? padTrack.notes.reduce((best, n) => (!best || n.length > best.length ? n : best), null)
    : null;

  if (!longest) {
    return [{ kind: "nudge", text: "Place a note on the A row and hold it for a bar." }];
  }

  const label = noteLabel(song, longest.pitch);
  const bars = longest.length / song.stepsPerBar;
  const seconds = Math.round(longest.length * stepSeconds(song));
  const slow = padTrack.attack === undefined || padTrack.attack >= 0.5;
  const swell = slow ? "slow" : "fast";

  return [
    {
      kind: "did",
      text: `You held ${label} for ${bars} ${bars === 1 ? "bar" : "bars"}, about ${seconds} seconds, with a ${swell} swell.`,
    },
    {
      kind: "why",
      text: slow
        ? "A slow attack lets the sound fade in, so it feels like it's breathing."
        : "A fast attack makes the note start right away, more like pressing a piano key.",
    },
    {
      kind: "nudge",
      text: !must.met
        ? must.hint
        : slow
        ? "Now switch to the fast swell and listen to how the note begins."
        : "Now try the slow swell and hear the note bloom.",
    },
  ];
};

const pairsNotes = (song, must) => {
  const pairs = soundingPairs(song);
  if (pairs.length === 0) {
    return [
      { kind: "did", text: "Your notes took turns, so no two sounded at once." },
      { kind: "nudge", text: must.hint },
    ];
  }

  const shown = pairs.slice(0, 3);
  const pairText = shown
    .map(
      (p) =>
        `${noteLabel(song, p.low)} with ${noteLabel(song, p.high)}: ${intervalName(p.semitones).name}`
    )
    .join("; ");
  const first = intervalName(pairs[0].semitones);

  return [
    { kind: "did", text: `You played ${pairText}.` },
    { kind: "why", text: `${capitalize(first.name)} sounds ${first.feel}.` },
    { kind: "nudge", text: pairsNudge(song, pairs) },
  ];
};

const homeNotes = (song) => {
  const bellTrack = song.tracks.find((t) => t.instrument === "bell");
  const rootLabel = noteLabel(song, song.rootMidi);
  const bellNotes = bellTrack ? bellTrack.notes : [];

  if (bellNotes.length === 0) {
    return [
      { kind: "nudge", text: `Add a bell melody over the pad, then end it on ${rootLabel}.` },
    ];
  }

  const last = lastNote(bellTrack);
  const lastLabel = noteLabel(song, last.pitch);
  const distinctClasses = new Set(bellNotes.map((n) => pc(n.pitch))).size;
  const noteWord = distinctClasses === 1 ? "1 of the 5 notes" : `${distinctClasses} of the 5 notes`;
  const isRoot = pc(last.pitch) === pc(song.rootMidi);

  let nudgeText;
  if (!isRoot) {
    nudgeText = `Move the last note to ${rootLabel} and listen for the difference.`;
  } else if (emptyBars(song, bellTrack.id) === 0) {
    nudgeText = "Try leaving one bar empty. Silence makes the notes around it stand out.";
  } else {
    nudgeText = `Next time, end on ${noteLabel(song, song.rootMidi + 3)} on purpose and hear the question.`;
  }

  return [
    { kind: "did", text: `Your bell melody used ${noteWord} and ended on ${lastLabel}.` },
    {
      kind: "why",
      text: isRoot
        ? "Ending on the root sounds settled. That feeling of arriving home is called release."
        : `Ending on ${lastLabel} leaves the phrase hanging, like a question. That pull is called tension.`,
    },
    { kind: "nudge", text: nudgeText },
  ];
};

// Joins short phrases the way a sentence would: "A", "A and B", "A, B and C".
const joinList = (parts) => {
  if (parts.length <= 1) return parts.join("");
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
};

const pulseNotes = (song, must) => {
  const drumsTrack = song.tracks.find((t) => t.instrument === "drums");
  const notes = drumsTrack ? drumsTrack.notes : [];
  const kicks = notes.filter((n) => n.pitch === KICK);
  const snares = notes.filter((n) => n.pitch === SNARE);
  const hats = notes.filter((n) => n.pitch === HAT);

  if (notes.length === 0) {
    return [
      {
        kind: "nudge",
        text: "Click a few squares on the drum grid, starting with the Kick row.",
      },
    ];
  }

  const beatStep = song.stepsPerBar / 4;
  const onBeat = (step) => step % beatStep === 0;

  const parts = [];
  if (kicks.length > 0) parts.push(`${kicks.length} ${kicks.length === 1 ? "kick" : "kicks"}`);
  if (snares.length > 0) parts.push(`${snares.length} ${snares.length === 1 ? "snare" : "snares"}`);
  if (hats.length > 0) parts.push(`${hats.length} ${hats.length === 1 ? "hat" : "hats"}`);

  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (hats.every((n) => onBeat(n.step))) {
    nudgeText =
      "Try hats on the “ands” between the beats. Off-beats make a groove feel like it's moving.";
  } else if (song.bpm === 72) {
    nudgeText = "Try the tempo slider. The same pattern at 90 bpm feels like a different song.";
  } else {
    nudgeText = "Try taking one kick away and hear how the pulse stumbles.";
  }

  return [
    { kind: "did", text: `You played ${joinList(parts)} at ${song.bpm} bpm.` },
    {
      kind: "why",
      text: must.met
        ? "A kick on beat 1 of every bar marks where each bar starts, so your ear can count along."
        : "Without a kick on every beat 1, the bars blur together and the ear loses its place.",
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const lowEndNotes = (song, must) => {
  const bassTrack = song.tracks.find((t) => t.instrument === "bass");
  const rootLabel = noteLabel(song, song.rootMidi);
  const notes = bassTrack ? bassTrack.notes : [];

  if (notes.length === 0) {
    return [
      { kind: "nudge", text: `Add a bass line under the loop, starting on ${rootLabel}.` },
    ];
  }

  const first = firstNote(bassTrack);
  const firstLabel = noteLabel(song, first.pitch);
  const n = notes.length;

  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (notes.every((note) => pc(note.pitch) === pc(song.rootMidi))) {
    nudgeText = `Try moving one bass note to ${noteLabel(song, song.rootMidi + 7)} or ${noteLabel(
      song,
      song.rootMidi + 5
    )} and feel the ground shift.`;
  } else {
    nudgeText = "Mute the bass for a moment and listen to what disappears.";
  }

  return [
    {
      kind: "did",
      text: `Your bass played ${n} ${n === 1 ? "note" : "notes"} and started on ${firstLabel}.`,
    },
    {
      kind: "why",
      text: must.met
        ? "Starting the bass on the root tells your ear where home is before the melody says anything."
        : `Starting the bass on ${firstLabel} makes the loop feel off-center, because the ear hears the lowest note as the ground.`,
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const twoMoodsNotes = (song, must) => {
  const padTrack = song.tracks.find((t) => t.instrument === "pad");
  const bars = [];
  for (let bar = 0; bar < song.bars; bar += 1) {
    bars.push(padTrack ? chordInBar(song, padTrack.id, bar) : null);
  }

  if (bars.every((c) => !c)) {
    return [
      { kind: "nudge", text: "Pick a chord for each bar in the chord strip above the pad." },
    ];
  }

  const sequence = bars.map((c) => (c ? c.name : "–")).join(" → ");

  const bassTrack = song.tracks.find((t) => t.instrument === "bass");
  const missingBassRoot = bars.some((chord, bar) => {
    if (!chord) return false;
    const start = bar * song.stepsPerBar;
    const hasRoot = bassTrack
      ? bassTrack.notes.some(
          (n) => n.step <= start && start < n.step + n.length && pc(n.pitch) === chord.rootPc
        )
      : false;
    return !hasRoot;
  });

  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (missingBassRoot) {
    nudgeText =
      "Put a bass note on each chord's root, A under Am and F under F, and the chords will sound fuller.";
  } else {
    nudgeText = "Swap which chord comes first and listen to how the mood flips.";
  }

  return [
    { kind: "did", text: `Your loop goes ${sequence}.` },
    {
      kind: "why",
      text: must.met
        ? "Am is a minor chord and sounds wistful; F is major and sounds warmer. Moving between them is the simplest way to make a loop feel like it's going somewhere."
        : "Staying on one chord keeps the mood still. A second chord is what makes a loop feel like it's going somewhere.",
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const round1 = (n) => Math.round(n * 10) / 10;

const nebulaNotes = (song, must) => {
  const padTrack = findTrack(song, "pad");
  const padPct = pct(soundOf(padTrack || { instrument: "pad" }).reverb);
  const others = ["bell", "bass", "drums"]
    .map((instrument) => ({ instrument, track: findTrack(song, instrument) }))
    .filter((o) => o.track);
  const parts = others.map((o) => `the ${o.instrument} ${pct(soundOf(o.track).reverb)}%`);
  const did = parts.length
    ? `Your pad is ${padPct}% wet, ${joinList(parts)}.`
    : `Your pad is ${padPct}% wet.`;

  const bassTrack = findTrack(song, "bass");
  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (bassTrack && soundOf(bassTrack).reverb > 0.3) {
    nudgeText =
      "Try less reverb on the bass. Low notes blur easily, and a dry bass keeps the ground solid.";
  } else if (!song.tracks.some((t) => soundOf(t).delay > 0.1)) {
    nudgeText = "Try some echo on the drums or bell and listen to the repeats land on the beat.";
  } else {
    nudgeText = "Try the loop with every reverb at zero, then bring them back. That's the room appearing.";
  }

  return [
    { kind: "did", text: did },
    {
      kind: "why",
      text: must.met
        ? "More reverb pushes a sound further back and blurs its edges, so the pad becomes a haze behind everything else."
        : "With little reverb the pad sounds close and dry, like it's in the room with you.",
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const driftNotes = (song, must) => {
  const padTrack = findTrack(song, "pad");
  const sound = soundOf(padTrack || { instrument: "pad" });
  const a = round1(sound.attack);
  const r = round1(sound.release);
  const b = pct(sound.brightness);
  const sweepText = sound.sweep ? "on" : "off";

  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (sound.brightness > 0.7) {
    nudgeText = "Try lowering the brightness so the sweep has further to travel.";
  } else {
    nudgeText = "Try a long fade so each chord melts into the next.";
  }

  return [
    {
      kind: "did",
      text: `Your pad swells in over ${a} seconds, fades over ${r} seconds, sits at ${b}% brightness, and its sweep is ${sweepText}.`,
    },
    {
      kind: "why",
      text: must.met
        ? "A slow swell and a rising filter make each pass of the loop feel like it's opening up, even though the notes don't change."
        : "A quick swell makes the pad start like a keyboard. Ambient pads usually fade in so you can't hear where they begin.",
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const gravityNotes = (song, must) => {
  const padTrack = findTrack(song, "pad");
  const bars = [];
  for (let bar = 0; bar < song.bars; bar += 1) {
    bars.push(padTrack ? chordInBar(song, padTrack.id, bar) : null);
  }

  if (bars.every((c) => !c)) {
    return [
      { kind: "nudge", text: "Pick a chord for each bar in the chord strip above the pad." },
    ];
  }

  const sequence = bars.map((c) => (c ? c.name : "–")).join(" → ");
  const last = bars[bars.length - 1];

  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (last && last.index === 6) {
    nudgeText = "Now try ending on F instead of G and listen to how the pull home gets softer.";
  } else {
    nudgeText = "Try putting G in the last bar, right before the loop comes back to Am.";
  }

  return [
    { kind: "did", text: `Your loop goes ${sequence}.` },
    {
      kind: "why",
      text: must.met
        ? "G sits just below Am, so it leans back toward home. Ending the loop on G makes the restart feel like arriving."
        : "With fewer chords the loop stays near home. Each new chord adds a different pull.",
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const satellitesNotes = (song, must) => {
  const bellTrack = findTrack(song, "bell");
  const notes = bellTrack ? bellTrack.notes : [];

  if (notes.length === 0) {
    return [
      {
        kind: "nudge",
        text: "Press Arpeggiate to fill the bell with chord notes, or place them by hand.",
      },
    ];
  }

  const { count, chordBars } = arpeggioStats(song);
  const n = notes.length;
  const noteWord = n === 1 ? "1 note" : `${n} notes`;

  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (soundOf(bellTrack).delay < 0.2) {
    nudgeText = "Turn the bell's echo up. Its repeats are timed to the beat and fill in the gaps.";
  } else {
    nudgeText = "Try deleting a few arpeggio notes. Small gaps make the pattern breathe.";
  }

  return [
    {
      kind: "did",
      text: `Your bell plays ${noteWord}, and ${count} of ${chordBars} chord bars are pure arpeggio.`,
    },
    {
      kind: "why",
      text: must.met
        ? "Every arpeggio note comes from the chord under it, so it can't clash. It adds motion without changing the harmony."
        : "Notes from outside the chord make the bell a melody of its own rather than an arpeggio. Both are fine; they just do different jobs.",
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const weightlessNotes = (song, must) => {
  const padTrack = findTrack(song, "pad");
  const barChords = [];
  for (let bar = 0; bar < song.bars; bar += 1) {
    const chord = padTrack ? chordInBar(song, padTrack.id, bar) : null;
    if (chord) barChords.push({ bar, chord });
  }

  if (barChords.length === 0) {
    return [
      { kind: "nudge", text: "Pick a chord for each bar in the chord strip above the pad." },
    ];
  }

  const susEntries = barChords.filter((e) => e.chord.variant);
  const s = susEntries.length;
  const c = barChords.length;
  const susNames = [...new Set(susEntries.map((e) => e.chord.name))];

  let did = `${s} of your ${c} chord bars are sus chords.`;
  if (s > 0) {
    did += ` You used ${joinList(susNames)}.`;
  }

  const bellTrack = findTrack(song, "bell");
  const bellMismatch =
    !!bellTrack &&
    bellTrack.notes.length > 0 &&
    barChords.some(({ bar, chord }) => {
      const start = bar * song.stepsPerBar;
      const end = start + song.stepsPerBar;
      return bellTrack.notes.some(
        (n) => n.step >= start && n.step < end && !chord.pcs.includes(pc(n.pitch))
      );
    });

  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (s === c) {
    nudgeText = "Try making just one chord plain again. The contrast makes the floating ones float more.";
  } else if (bellMismatch) {
    nudgeText = "Press Arpeggiate again so the bell follows the new sus chords.";
  } else {
    nudgeText = "Try sus4 on the G. It pulls toward home even harder.";
  }

  return [
    { kind: "did", text: did },
    {
      kind: "why",
      text: must.met
        ? "A sus chord has no third, so it's neither major nor minor. It hangs in the air instead of settling, which is why it sounds weightless."
        : "Every chord still has its third, so each one clearly sounds major or minor.",
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const lightYearsNotes = (song, must) => {
  const sections = song.sections;
  if (!Array.isArray(sections) || sections.length === 0) {
    return [{ kind: "nudge", text: "Switch a lane off in at least one section." }];
  }

  const counts = sections.map((sec) => {
    const start = sec.startBar * song.stepsPerBar;
    const end = start + sec.bars * song.stepsPerBar;
    return song.tracks.filter(
      (t) => !sec.off.includes(t.id) && t.notes.some((n) => n.step >= start && n.step < end)
    ).length;
  });
  const max = Math.max(...counts);

  const parts = sections.map((sec, i) =>
    i === 0
      ? `${sec.name} has ${counts[i]} ${counts[i] === 1 ? "lane" : "lanes"}`
      : `${sec.name} ${counts[i]}`
  );

  let nudgeText;
  if (!must.met) {
    nudgeText = must.hint;
  } else if (counts[0] >= max) {
    nudgeText = "Try a thinner intro, so there's somewhere to build to.";
  } else if (counts[counts.length - 1] < max) {
    nudgeText = "Try bringing everything back in the last section so it feels like a return.";
  } else {
    nudgeText = "Try switching off the drums in just one section and hear the floor drop away.";
  }

  return [
    { kind: "did", text: `${joinList(parts)}.` },
    {
      kind: "why",
      text: must.met
        ? "Changing which lanes play is the simplest way to make a track feel like a journey. Each section is a new chapter, even with the same notes."
        : "Every section has the same lanes, so the track stays at one level the whole way through.",
    },
    { kind: "nudge", text: nudgeText },
  ];
};

const transmissionNotes = (song, must) => {
  const n = song.tracks.filter((t) => t.notes.length > 0).length;
  const laneWord = n === 1 ? "lane" : "lanes";

  return [
    {
      kind: "did",
      text: `Your transmission uses ${n} ${laneWord} over ${song.bars} bars at ${song.bpm} bpm.`,
    },
    {
      kind: "why",
      text: "Sounds, notes, rhythm, chords, space and form: this track has all of them, and you chose every one.",
    },
    {
      kind: "nudge",
      text: !must.met
        ? must.hint
        : "Save it, then listen to a track you love and try to name what each layer is doing.",
    },
  ];
};

// The listen-back script for a finished tape: whether its must was met, and
// a few short notes about what happened, why it sounds that way, and what
// to try next.
export const listenBack = (tape, song) => {
  const must = checkMust(tape.must ? tape.must.id : undefined, song);

  let notes;
  if (tape.id === "hold") {
    notes = holdNotes(song, must);
  } else if (tape.id === "pairs") {
    notes = pairsNotes(song, must);
  } else if (tape.id === "home") {
    notes = homeNotes(song);
  } else if (tape.id === "pulse") {
    notes = pulseNotes(song, must);
  } else if (tape.id === "lowEnd") {
    notes = lowEndNotes(song, must);
  } else if (tape.id === "twoMoods") {
    notes = twoMoodsNotes(song, must);
  } else if (tape.id === "nebula") {
    notes = nebulaNotes(song, must);
  } else if (tape.id === "drift") {
    notes = driftNotes(song, must);
  } else if (tape.id === "gravity") {
    notes = gravityNotes(song, must);
  } else if (tape.id === "satellites") {
    notes = satellitesNotes(song, must);
  } else if (tape.id === "weightless") {
    notes = weightlessNotes(song, must);
  } else if (tape.id === "lightYears") {
    notes = lightYearsNotes(song, must);
  } else if (tape.id === "transmission") {
    notes = transmissionNotes(song, must);
  } else {
    notes = [{ kind: "did", text: "Nice. Your track is saved." }];
  }

  return { met: must.met, notes };
};
